import { set, flow, isUndefined, isNull, unset } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { CalculatorActionTypes, CalculatorFieldTypeUI, CalculatorManagerPage, CalculatorOptionType, CalculatorPadOptionUI, CalculatorPromptDB, CalculatorPromptUI, CalculatorState } from './types';
import { newCalculatorField } from '../../../shared/calculator/constants';

export const initialCalculatorPrompt = (): CalculatorPromptUI => ({
    calculatorFieldType: null,
    calculation: [newCalculatorField(null)],
    calculationText: '',
    priorityOrder: 1,
    isSystem: true
});

export const newCalculatorPadOption = (): CalculatorPadOptionUI => ({
    label: '',
    description: '',
    inputType: '',
    type: CalculatorOptionType.STANDARD,
    config: {
        fontSize: '14px',
        inputFontSize: '14px'
    }
});

export const newCalculatorFieldType = (): CalculatorFieldTypeUI => ({
    name: '',
    description: '',
    buildPadOptions: [],
    outputPadOptions: [],
    gracePeriodPadOptions: [],
    includeOutput: 0,
    includeGracePeriod: 0,
    includeOther: 0,
    includeConditionals: 0,
    includeIfElse: 0
});

const stripCalculatorPrompt = (calculatorPrompt: CalculatorPromptDB): CalculatorPromptUI => flow(
    unset('createdBy'),
    unset('createdByUsername'),
    unset('createdDate'),
    unset('clientId')
)(calculatorPrompt);

export const INITIAL_STATE: CalculatorState = {
    calculatorManagerPage: CalculatorManagerPage.SELECT,
    calculatorFields: [],
    padOptions: [],
    padOptionsModalOpen: false,
    currentCalculatorField: null,
    savedCalculatorField: null,
    currentPadOption: null,
    savedPadOption: null,
    calculatorFieldHasUpdated: false,
    padOptionHasUpdated: false,
    isFetchingCalculatorPrompts: false,
    isSavingCalculatorPrompt: false,
    isDeletingCalculatorPrompt: false,
    calculatorPrompts: [],
    selectedCalculatorPrompt: null,
    calculatorPromptToDelete: null,
    selectedPromptUpdated: false,
    showCalculatorPreview: false,
    currentSelectedFieldTypePromptOrder: [],
    savedSelectedFieldTypePromptOrder: []
};

export const calculatorReducer: Reducer<CalculatorState> = (state = INITIAL_STATE, { type, payload }): CalculatorState => {
    switch (type) {
        case CalculatorActionTypes.SET_ALL_CALCULATOR_FIELDS:
            return flow(
                set('calculatorFields', payload.calculatorFields),
                set('padOptions', payload.padOptions)
            )(state);
        case CalculatorActionTypes.SET_CALCULATOR_MANAGER_PAGE:
            return set('calculatorManagerPage', payload, state);
        case CalculatorActionTypes.TOGGLE_CALCULATOR_PAD_OPTIONS_MODAL:
            return flow(
                set('padOptionsModalOpen', payload),
                set('currentPadOption', null),
                set('savedPadOption', null),
                set('padOptionHasUpdated', false)
            )(state);
        case CalculatorActionTypes.SELECT_CALCULATOR_PAD_OPTION: {
            let padOption = newCalculatorPadOption();
            const existingPadOptionSelected = state.padOptions.find(({ calculatorPadOptionId }) => calculatorPadOptionId === payload);
            if (existingPadOptionSelected) {
                padOption = existingPadOptionSelected;
            }
            return flow(
                set('currentPadOption', padOption),
                set('savedPadOption', padOption),
                set('padOptionHasUpdated', false)
            )(state);
        }
        case CalculatorActionTypes.UPDATE_CALCULATOR_PAD_OPTION_VALUE: {
            const { key, value } = payload;
            return set(`currentPadOption[${key}]`, value, state);
        }
        case CalculatorActionTypes.SET_PAD_OPTION_HAS_UPDATED:
            return set('padOptionHasUpdated', payload, state);
        case CalculatorActionTypes.UPSERT_CALCULATOR_PAD_OPTION_SUCCESSFUL:
            return flow(
                set('padOptionHasUpdated', false),
                set('padOptions', payload),
                set('savedPadOption', state.currentPadOption)
            )(state);
        case CalculatorActionTypes.SELECT_CALCULATOR_FIELD_TYPE: {
            let calculatorField: CalculatorFieldTypeUI | null = newCalculatorFieldType();
            const existingCalculatorFieldSelected = state.calculatorFields.find(({ calculatorFieldTypeId }) => calculatorFieldTypeId === payload);
            if (existingCalculatorFieldSelected) {
                calculatorField = existingCalculatorFieldSelected;
            }
            if (isNull(payload)) {
                calculatorField = null;
            }
            return flow(
                set('currentCalculatorField', calculatorField),
                set('savedCalculatorField', calculatorField),
                set('calculatorFieldHasUpdated', false),
                set('showCalculatorPreview', false)
            )(state);
        }
        case CalculatorActionTypes.UPDATE_CALCULATOR_FIELD_TYPE_VALUE: {
            const { key, value } = payload;
            return set(`currentCalculatorField[${key}]`, value, state);
        }
        case CalculatorActionTypes.SET_FIELD_TYPE_HAS_UPDATED:
            return set('calculatorFieldHasUpdated', payload, state);
        case CalculatorActionTypes.UPSERT_CALCULATOR_FIELD_TYPE_SUCCESSFUL:
            return flow(
                set('calculatorFieldHasUpdated', false),
                set('calculatorFields', payload),
                set('savedCalculatorField', null),
                set('currentCalculatorField', null),
                set('showCalculatorPreview', false)
            )(state);
        case CalculatorActionTypes.FETCH_CALCULATOR_PROMPT_EXAMPLES_STARTED:
            return set('isFetchingCalculatorPrompts', true, state);
        case CalculatorActionTypes.FETCH_CALCULATOR_PROMPT_EXAMPLES_SUCCESSFUL:
            return flow(
                set('isFetchingCalculatorPrompts', false),
                set('calculatorPrompts', payload)
            )(state);
        case CalculatorActionTypes.FETCH_CALCULATOR_PROMPT_EXAMPLES_FAILED:
            return set('isFetchingCalculatorPrompts', false, state);
        case CalculatorActionTypes.UPSERT_CALCULATOR_PROMPT_STARTED:
            return set('isSavingCalculatorPrompt', true, state);
        case CalculatorActionTypes.UPSERT_CALCULATOR_PROMPT_SUCCESSFUL:
            return flow(
                set('isSavingCalculatorPrompt', false),
                set('selectedCalculatorPrompt', null),
                set('calculatorPrompts', payload)
            )(state);
        case CalculatorActionTypes.UPSERT_CALCULATOR_PROMPT_FAILED:
            return set('isSavingCalculatorPrompt', false, state);
        case CalculatorActionTypes.DELETE_CALCULATOR_PROMPT_STARTED:
            return set('isDeletingCalculatorPrompt', true, state);
        case CalculatorActionTypes.DELETE_CALCULATOR_PROMPT_SUCCESSFUL:
            return flow(
                set('isDeletingCalculatorPrompt', false),
                set('selectedCalculatorPrompt', null),
                set('calculatorPromptToDelete', null),
                set('calculatorPrompts', payload)
            )(state);
        case CalculatorActionTypes.DELETE_CALCULATOR_PROMPT_FAILED:
            return set('isDeletingCalculatorPrompt', false, state);
        case CalculatorActionTypes.SELECT_CALCULATOR_PROMPT: {
            let selectedCalculatorPrompt = initialCalculatorPrompt();
            if (isNull(payload)) {
                return set('selectedCalculatorPrompt', null, state);
            }
            if (!isUndefined(payload)) {
                selectedCalculatorPrompt = stripCalculatorPrompt(state.calculatorPrompts.find(({ promptId }) => promptId === payload)!);
            }
            return set('selectedCalculatorPrompt', selectedCalculatorPrompt, state);
        }
        case CalculatorActionTypes.TOGGLE_DELETE_CALCULATOR_PROMPT_CONFIRMATION_MODAL: {
            const promptToDelete = !isNull(payload) ? state.calculatorPrompts.find(({ promptId }) => promptId === payload)! : null;
            return set('calculatorPromptToDelete', promptToDelete, state);
        }
        case CalculatorActionTypes.UPDATE_SELECTED_CALCULATOR_PROMPT:
            return set('selectedCalculatorPrompt', payload, state);
        case CalculatorActionTypes.SET_SELECTED_CALCULATOR_PROMPT_UPDATED:
            return set('selectedPromptUpdated', payload, state);
        case CalculatorActionTypes.TOGGLE_CALCULATOR_FIELD_TYPE_PREVIEW_VIEW:
            return set('showCalculatorPreview', payload, state);
        case CalculatorActionTypes.SET_CALCULATOR_PROMPT_ORDER:
            return flow(
                set('currentSelectedFieldTypePromptOrder', payload),
                set('savedSelectedFieldTypePromptOrder', payload)
            )(state);
        case CalculatorActionTypes.UPDATE_CALCULATOR_PROMPT_ORDER:
            return set('currentSelectedFieldTypePromptOrder', payload, state);
        case CalculatorActionTypes.SAVE_PROMPT_ORDER_STARTED:
            return set('isFetchingCalculatorPrompts', true, state);
        case CalculatorActionTypes.SAVE_PROMPT_ORDER_SUCCESSFUL:
            return flow(
                set('isFetchingCalculatorPrompts', false),
                set('calculatorPrompts', payload)
            )(state);
        case CalculatorActionTypes.SAVE_PROMPT_ORDER_FAILED:
            return set('isFetchingCalculatorPrompts', false, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
